<template>
  <div v-if="!mqttOnly" class="message-center-poptip">
    <div class="msg-poptip-header">
      <div class="msg-poptip-header-left">
        <span class="msg-poptip-title">{{ $t('message.center') }}</span>
        <span v-if="unreadCount > 0" class="msg-poptip-badge">{{ $t('message.unreadCount', { n: unreadCount }) }}</span>
      </div>
      <div class="a-link" @click="goToCenter">{{ $t('message.viewAll') }}</div>
    </div>
    <div class="msg-poptip-list" v-if="list.length">
      <div
        v-for="(item, index) in list"
        :key="item.id"
        class="msg-poptip-item"
      >
        <div class="msg-poptip-item-main" @click="onItemClick(item)">
          <div class="msg-poptip-item-icon" :class="'type-' + (item.type || 'system')">
            <Icon :type="typeIcons[item.type] || 'ios-notifications-outline'" size="16" />
          </div>
          <div class="msg-poptip-item-body">
            <div class="msg-poptip-item-title-row">
              <span class="msg-poptip-item-title">{{ item.title }}</span>
              <span class="msg-poptip-item-status" :class="item.is_read ? 'read' : 'unread'">{{ item.is_read ? $t('message.read') : $t('message.unread') }}</span>
            </div>
            <div class="msg-poptip-item-desc">{{ item.content }}</div>
            <div class="msg-poptip-item-time">{{ formatTime(item.create_time) }}</div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="!loading" class="msg-poptip-empty">
      <Icon type="ios-mail-open-outline" size="40" color="#c5c8ce" />
      <p>{{ $t('message.noData') }}</p>
    </div>
    <Spin v-if="loading" size="small" fix />
    <div class="msg-poptip-footer">
      <div class="msg-poptip-footer-row">
        <Button v-if="unreadCount > 0" type="text" size="small" :loading="markingAll" @click="markAllRead">全部已读</Button>
        <div class="msg-poptip-setting">
          <span class="msg-poptip-setting-label">新消息提示音</span>
          <i-switch v-model="soundEnabled" @on-change="onSoundChange" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import 'moment/locale/zh-cn'
import 'moment/locale/ko'
import md5 from 'js-md5'

// i18n locale -> moment locale
const MOMENT_LOCALE_MAP = { 'zh-cn': 'zh-cn', 'en-us': 'en', 'ko-kr': 'ko' }

const STORAGE_KEY = 'MSG_NOTIFY_SOUND_ENABLED'
const typeIcons = {
  order: 'ios-cart-outline',
  system: 'ios-notifications-outline',
  didi_expire: 'ios-time-outline',
  product: 'ios-pricetag-outline',
  refund: 'ios-undo-outline'
}

export default {
  name: 'MessageCenterPoptip',
  props: {
    messageCenterPath: {
      type: String,
      default: '/apply/message/center'
    },
    /** 仅用于 MQTT 订阅（不渲染面板），由 MqttNotice 传入 */
    mqttClient: {
      type: Object,
      default: null
    },
    /** 为 true 时只执行 MQTT 订阅逻辑，不渲染气泡内容 */
    mqttOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      list: [],
      loading: false,
      markingAll: false,
      unreadCount: 0,
      typeIcons,
      soundEnabled: localStorage.getItem(STORAGE_KEY) !== '0',
      // MQTT 相关
      subscribed: false,
      currentTopic: null,
      messageHandler: null,
      audio: null
    }
  },
  watch: {
    mqttClient: {
      handler (client) {
        if (this.mqttOnly && client && client.connected) {
          this.subscribe()
        }
      },
      immediate: true
    }
  },
  mounted () {
    if (this.mqttOnly && this.mqttClient && this.mqttClient.connected) {
      this.subscribe()
    }
  },
  beforeDestroy () {
    if (this.mqttOnly) {
      this.unsubscribe()
    }
  },
  methods: {
    // ---------- 气泡面板 ----------
    fetchList () {
      this.soundEnabled = localStorage.getItem(STORAGE_KEY) !== '0'
      this.loading = true
      this.requestApi('/adm/getMessageList', {
        page: 1,
        page_size: 5
      }).then(res => {
        this.loading = false
        if (res.status === 1 && res.data) {
          this.list = res.data.list || []
        }
      }).catch(() => { this.loading = false })
      this.fetchUnreadCount()
    },
    fetchUnreadCount () {
      this.requestApi('/adm/getMessageUnreadCount', {}).then(res => {
        if (res.status === 1 && res.data && res.data.count !== undefined) {
          this.unreadCount = res.data.count
          this.$store.commit('SET_MESSAGE_UNREAD', res.data.count)
        }
      })
    },
    formatTime (str) {
      if (!str) return ''
      const locale = MOMENT_LOCALE_MAP[this.$i18n.locale] || 'en'
      const m = moment(str).locale(locale)
      if (m.isAfter(moment().subtract(1, 'day'))) return m.fromNow()
      if (m.isAfter(moment().subtract(7, 'day'))) return m.format('ddd HH:mm')
      return m.format('MM-DD HH:mm')
    },
    onItemClick (item) {
      if (!item.is_read) {
        this.requestApi('/adm/markMessageRead', { ids: [item.id] }).then(res => {
          if (res.status === 1) {
            item.is_read = 1
            this.fetchUnreadCount()
          }
        })
      }
      const path = (item.extra && item.extra.path) || this.messageCenterPath
      const query = {}
      if (item.extra && item.extra.order_id) query.id = item.extra.order_id
      if (item.extra && item.extra.group_id) query.id = item.extra.group_id
      this.toUrl(path, query)
    },
    markAllRead () {
      this.markingAll = true
      this.requestApi('/adm/markMessageAllRead', {}).then(res => {
        this.markingAll = false
        if (res.status === 1) {
          this.unreadCount = 0
          this.$store.commit('SET_MESSAGE_UNREAD', 0)
          this.fetchList()
        }
      }).catch(() => { this.markingAll = false })
    },
    goToCenter () {
      this.toUrl(this.messageCenterPath)
    },
    onSoundChange (val) {
      localStorage.setItem(STORAGE_KEY, val ? '1' : '0')
    },
    // ---------- MQTT 订阅（mqttOnly 时使用） ----------
    playNotifySound () {
      if (localStorage.getItem(STORAGE_KEY) === '0') return
      try {
        if (!this.audio) {
          this.audio = new Audio(require('@/assets/mp3/ts.wav'))
        }
        this.audio.currentTime = 0
        this.audio.play().catch(() => {})
      } catch (e) {}
    },
    subscribe () {
      if (!this.mqttClient || !this.mqttClient.connected) return
      if (this.subscribed && this.currentTopic) this.unsubscribe()

      const applyId = (this.$store.state.applyInfo && this.$store.state.applyInfo.apply_id) ||
        (this.$store.state.userInfo && this.$store.state.userInfo.applyId) || 0
      if (!applyId) return

      const institutionId = this.$store.state.labelType === 2 && this.$store.state.insInfo
        ? this.$store.state.insInfo.id
        : 0

      // Topic 与后端 AdminMessageService 一致：domain 需与后端 API_URL 基址（无末尾斜杠）一致，否则收不到推送
      const domain = this.CurrentDomain || window.location.origin
      const topic = md5(domain) + `/Admin/Message/apply_${applyId}/institution_${institutionId}`

      if (this.subscribed && this.currentTopic === topic) return
      if (this.messageHandler) this.mqttClient.removeListener('message', this.messageHandler)

      this.messageHandler = (tpc, message) => {
        if (tpc !== topic) return
        try {
          const payload = JSON.parse(message.toString())
          this.$store.commit('INCREMENT_MESSAGE_UNREAD')
          this.playNotifySound()
          this.$Message.info({
            content: payload.title || this.$t('message.newMessage'),
            duration: 3
          })
        } catch (e) {
          console.error('AdminMessage parse failed:', e)
        }
      }

      this.mqttClient.on('message', this.messageHandler)
      this.mqttClient.subscribe(topic, { qos: 1 }, (err) => {
        if (!err) {
          this.subscribed = true
          this.currentTopic = topic
        }
      })
    },
    unsubscribe () {
      if (!this.mqttClient) return
      if (this.messageHandler) {
        this.mqttClient.removeListener('message', this.messageHandler)
        this.messageHandler = null
      }
      if (this.currentTopic) {
        this.mqttClient.unsubscribe(this.currentTopic)
        this.currentTopic = null
      }
      this.subscribed = false
    }
  }
}
</script>

<style scoped>
.message-center-poptip {
  max-height: 480px;
  display: flex;
  flex-direction: column;
}
.msg-poptip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}
.msg-poptip-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.msg-poptip-title {
  font-size: 15px;
  font-weight: 600;
  color: #17233d;
}
.msg-poptip-badge {
  font-size: 12px;
  color: #2d8cf0;
}
.msg-poptip-list {
  flex: 1;
  overflow-y: auto;
  max-height: 280px;
}
.msg-poptip-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px;
  border-bottom: 1px solid #e8eaec;
  transition: background 0.2s;
}
.msg-poptip-item:last-child {
  border-bottom: none;
}
.msg-poptip-item-main {
  flex: 1;
  display: flex;
  align-items: flex-start;
  min-width: 0;
  cursor: pointer;
}
.msg-poptip-item-main:hover {
  opacity: 0.9;
}
.msg-poptip-item-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
}
.msg-poptip-item-status {
  flex-shrink: 0;
  font-size: 11px;
}
.msg-poptip-item-status.unread {
  color: #ed4014;
}
.msg-poptip-item-status.read {
  color: #c5c8ce;
}
.msg-poptip-item-read-btn {
  flex-shrink: 0;
  margin-left: 8px;
  color: #2d8cf0;
  padding: 0 6px;
}
.msg-poptip-item-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 10px;
}
.msg-poptip-item-icon.type-order { background: #e6f7ff; color: #1890ff; }
.msg-poptip-item-icon.type-system { background: #fff7e6; color: #fa8c16; }
.msg-poptip-item-icon.type-didi_expire { background: #fff1f0; color: #f5222d; }
.msg-poptip-item-icon.type-product { background: #f6ffed; color: #52c41a; }
.msg-poptip-item-icon.type-refund { background: #fffbe6; color: #faad14; }
.msg-poptip-item-body {
  flex: 1;
  min-width: 0;
}
.msg-poptip-item-title {
  font-size: 13px;
  font-weight: 500;
  color: #17233d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
.msg-poptip-item-desc {
  font-size: 12px;
  color: #808695;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 2px;
}
.msg-poptip-item-time {
  font-size: 11px;
  color: #c5c8ce;
}
.msg-poptip-empty {
  padding: 32px 16px;
  text-align: center;
  color: #c5c8ce;
  font-size: 13px;
}
.msg-poptip-empty p { margin-top: 8px; }
.msg-poptip-footer {
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px solid #f0f0f0;
}
.msg-poptip-footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.msg-poptip-setting {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #515a6e;
}
.msg-poptip-setting-label {
  color: #515a6e;
}
</style>



// WEBPACK FOOTER //
// src/components/msgNotice/MessageCenterPoptip.vue